import React, { useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import axios from "axios";
import QuickTrainerConfirmDialog from "./QuickTrainerConfirmDialog";
import {
  correctQuickTrainerIntakeRow, confirmReceivedQuickRowNearMint, fetchQuickTrainerIntakeAudit,
  finaliseQuickTrainerIntake, voidQuickTrainerIntakeRow,
} from "./trainerInventoryApi";
import { isExactTrainerCard, positiveQuantity, quickBatchTotals, quickFinaliseSnapshot } from "./quickTrainerIntakeUtils";

const FINISHES = ["Normal", "Reverse Holo", "Holo"];
const SOURCES = ["bulk_lot", "standard", "pack_rip", "targeted_buy"];
const message = (error) => error?.response?.data?.error || error?.message || "Request failed";

const editable = (row, batchOpen) => batchOpen && !row.voided_at && !row.inventory_applied_at &&
  !row.allocation_applied_at && Boolean(row.catalog_card_id && row.catalog_set_name && row.request_id) &&
  !Number(row.single_allocation_quantity || 0) && !Number(row.playset_allocation_quantity || 0);
const initialDraft = (row) => ({
  catalog_card_id: row.catalog_card_id, name_and_number: row.card_name, set_name: row.catalog_set_name,
  card_variant: row.card_variant, quantity: String(row.quantity), unit_cost: row.unit_cost == null ? "" : String(row.unit_cost),
  source_type: row.source_type || "bulk_lot", source_reference: row.source_reference || "",
  storage_location: row.storage_location || "", notes: row.notes || "",
  card_condition: "Near Mint", reason: "",
});

export default function QuickTrainerReceiptReview({ batchId, batchOpen, rows, disabled, reloadRows, refreshBatches }) {
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [lookup, setLookup] = useState("");
  const [matches, setMatches] = useState([]);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [zeroCostReviewed, setZeroCostReviewed] = useState(false);
  const [finaliseDialog, setFinaliseDialog] = useState(false);
  const [nearMintConfirmed, setNearMintConfirmed] = useState(false);
  const [finaliseUncertain, setFinaliseUncertain] = useState(false);
  const inFlight = useRef(false);
  const totals = quickBatchTotals(rows);
  const pending = rows.filter((row) => !row.voided_at && !row.inventory_applied_at);
  const unresolved = pending.filter((row) => !row.catalog_card_id || !row.catalog_set_name);
  const unknownQuickConditions = pending.filter((row) => row.request_id &&
    !["near mint", "nm"].includes(String(row.card_condition || "").trim().toLowerCase()));
  const zeroCostRows = pending.filter((row) => Number(row.unit_cost) === 0);
  const costInvalidRows = pending.filter((row) => row.unit_cost === null || row.unit_cost === undefined ||
    String(row.unit_cost).trim() === "" || !Number.isFinite(Number(row.unit_cost)) || Number(row.unit_cost) < 0);
  const canChange = batchOpen && !disabled && !busy;
  const editValid = draft && positiveQuantity(draft.quantity) !== null &&
    FINISHES.includes(draft.card_variant) && Boolean(draft.catalog_card_id && draft.name_and_number && draft.set_name) &&
    String(draft.unit_cost).trim() !== "" && Number.isFinite(Number(draft.unit_cost)) && Number(draft.unit_cost) >= 0 &&
    Number(draft.unit_cost) <= 999999.9999 &&
    Number(draft.unit_cost) * Number(draft.quantity) <= 99999999.99 &&
    draft.reason.trim().length >= 3 && draft.reason.trim().length <= 500 &&
    [draft.source_reference, draft.storage_location].every((value, i) =>
      String(value || "").length <= [255, 128][i]);

  const reset = () => {
    setActive(null); setDraft(null); setMatches([]); setLookup(""); setAudit(null); setNearMintConfirmed(false);
  };
  const selectEdit = (row) => {
    if (!editable(row, batchOpen) || disabled) return;
    setActive({ row, mode: "correct" }); setDraft(initialDraft(row));
    setError(""); setSuccess(""); setAudit(null); setMatches([]); setLookup("");
  };
  const selectVoid = (row) => {
    if (!editable(row, batchOpen) || disabled) return;
    setActive({ row, mode: "void" }); setDraft({ reason: "" });
    setError(""); setSuccess(""); setAudit(null);
  };
  const selectNearMintReview = (row) => {
    if (disabled || busy || !row.inventory_applied_at || row.voided_at ||
        !row.catalog_card_id || !row.request_id || String(row.card_condition || "").trim()) return;
    setActive({ row, mode: "confirm_nm" }); setDraft({ reason: "" });
    setNearMintConfirmed(false); setError(""); setSuccess(""); setAudit(null);
  };
  const search = async () => {
    if (lookup.trim().length < 2 || busy) return;
    setBusy(true); setMatches([]); setError("");
    try {
      const response = await axios.get("/api/cards/lookup", { params: { q: lookup.trim(), limit: 24, high_only: 0 } });
      setMatches((response.data?.data || []).filter(isExactTrainerCard));
    } catch (e) { setError(`Find Card failed: ${message(e)}`); }
    finally { setBusy(false); }
  };
  const saveChange = async () => {
    if (inFlight.current || disabled || busy || !active || !draft?.reason?.trim()) return;
    if (active.mode !== "confirm_nm" && !canChange) return;
    if (active.mode === "confirm_nm" && !nearMintConfirmed) return;
    if (active.mode === "correct" && !editValid) return;
    inFlight.current = true; setBusy(true); setError(""); setSuccess("");
    try {
      const { row, mode } = active;
      const payload = { ...draft, expected_revision: Number(row.revision || 1), reason: draft.reason.trim() };
      // The visible row-edit/remove dialog is already the explicit confirmation.
      // No native browser popup; Cancel/Close never submits a mutation.
      if (mode === "void") await voidQuickTrainerIntakeRow(batchId, row.id, payload);
      else if (mode === "confirm_nm") await confirmReceivedQuickRowNearMint(batchId, row.id, {
        expected_revision: Number(row.revision || 1), reason: draft.reason.trim(), confirm_near_mint: true,
      });
      else await correctQuickTrainerIntakeRow(batchId, row.id, payload);
      reset();
      setSuccess(mode === "void" ? `Row #${row.id} removed from staged totals, with audit history retained.` :
        mode === "confirm_nm" ? `Receipt #${row.id}: Near Mint recorded with an audit event; no stock or eBay update. Review the receipt and listing identity before eBay replenishment.` :
        `Row #${row.id} corrected. Verify the revised staged quantity before finalisation.`);
      await reloadRows();
      await refreshBatches();
      setConfirm(false);
    } catch (e) {
      setError(`Review change failed: ${message(e)}. Refresh batch if another operator changed it.`);
    } finally { inFlight.current = false; setBusy(false); }
  };
  const showAudit = async (row) => {
    if (busy) return;
    setBusy(true); setAudit(null); setActive({ row, mode: "audit" }); setError("");
    try {
      const response = await fetchQuickTrainerIntakeAudit(batchId, row.id);
      setAudit(response.data?.events || []);
    } catch (e) { setError(`Audit history unavailable: ${message(e)}`); }
    finally { setBusy(false); }
  };
  const finalise = async () => {
    if (inFlight.current || !canChange || !confirm || !pending.length || unresolved.length || unknownQuickConditions.length ||
        costInvalidRows.length || (zeroCostRows.length && !zeroCostReviewed)) return;
    setFinaliseDialog(false);
    inFlight.current = true; setBusy(true); setError(""); setSuccess("");
    const expected_snapshot = quickFinaliseSnapshot(rows);
    try {
      const response = await finaliseQuickTrainerIntake(batchId, {
        confirm_physical_receipt: true, expected_snapshot,
        confirm_zero_unit_costs: zeroCostRows.length > 0 && zeroCostReviewed,
      });
      if (response.data?.success !== true) throw new Error("Finalise Stock was not confirmed");
      const { already_finalised, rows_applied, quantity_applied } = response.data;
      setSuccess(already_finalised ? "Batch was already finalised. No extra physical stock was added." :
        `Physical receipt confirmed: ${rows_applied} rows / ${quantity_applied} cards. No eBay listing changed. Proceed to eBay Quantity Review; no separate allocation is required.`);
      setFinaliseUncertain(false); setConfirm(false); setZeroCostReviewed(false);
      await reloadRows(); await refreshBatches();
    } catch (e) {
      if (!e?.response || e.response.status >= 500) setFinaliseUncertain(true);
      setError(`Finalise not confirmed: ${message(e)}. Refresh batch to establish whether stock was received before retrying. The endpoint prevents repeat physical application.`);
    } finally { inFlight.current = false; setBusy(false); }
  };

  return <Stack spacing={2}>
    <Typography variant="h6">4. Review staged receipts and physically receive stock</Typography>
    <Typography variant="body2" color="text.secondary">
      Each photograph submission stays as its own row, even when the exact card and finish repeat. Edits and removals require a reason and are audited. Received lots cannot be changed here.
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip color="warning" label={`Pending: ${pending.length} rows / ${totals.staged} cards`} />
      <Chip color="success" label={`Received: ${totals.received} cards`} />
      <Chip label={`Removed: ${totals.voidedRows} rows / ${totals.voided} cards`} />
      <Chip color={unresolved.length ? "error" : "default"} label={`Needs review: ${unresolved.length}`} />
    </Stack>
    {error && <Alert severity="error" role="alert">{error}</Alert>}
    {success && <Alert severity="success" role="status">{success}</Alert>}
    {finaliseUncertain && <Alert severity="warning">Previous Finalise Stock response was uncertain. Refresh batch and verify its status and quantities before submitting again.</Alert>}
    {zeroCostRows.length > 0 && <Alert severity="warning" role="alert">
      {zeroCostRows.length} staged row(s) have a unit cost of $0.0000. These may include older receipts entered before cost review was required. Correct any unintended zero costs BEFORE receiving stock.
    </Alert>}
    {unknownQuickConditions.length > 0 && <Alert severity="warning" role="alert">
      {unknownQuickConditions.length} older staged Quick Intake receipt(s) have unknown or non-Near Mint condition. Open Correct, verify the cards are Near Mint, and save an audited correction before finalising.
    </Alert>}
    {costInvalidRows.length > 0 && <Alert severity="error">{costInvalidRows.length} staged row(s) have missing or invalid unit costs. Correct them before finalisation.</Alert>}
    <TableContainer sx={{ maxHeight: 460 }}>
      <Table size="small" stickyHeader aria-label="Staged trainer receipt review">
        <TableHead><TableRow>
          <TableCell>Row / photo</TableCell><TableCell>Exact card / set</TableCell><TableCell>Finish</TableCell>
          <TableCell align="right">Qty</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((row) => {
            const received = Boolean(row.inventory_applied_at);
            const removed = Boolean(row.voided_at);
            const needsReview = !removed && !received && (!row.catalog_card_id || !row.catalog_set_name);
            const canEdit = editable(row, batchOpen) && !disabled && !busy;
            return <TableRow key={row.id} sx={removed ? { opacity: 0.6 } : {}}>
              <TableCell>#{row.id}<Typography variant="caption" display="block">{row.source_reference || "No photo ref"}</Typography></TableCell>
              <TableCell>{row.card_name}<Typography variant="caption" display="block">{row.catalog_set_name || "Legacy/unknown catalogue set"}</Typography></TableCell>
              <TableCell>{row.card_variant}</TableCell><TableCell align="right">{row.quantity}</TableCell>
              <TableCell><Chip size="small" color={removed ? "default" : received ? "success" : needsReview ? "error" : "warning"}
                label={removed ? "Removed" : received ? "Received" : needsReview ? "Needs review" : "Staged"} /></TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {received && !removed && !String(row.card_condition || "").trim() && Boolean(row.catalog_card_id && row.request_id) &&
                    <Button color="warning" size="small" disabled={disabled || busy} onClick={() => selectNearMintReview(row)}>
                      Verify NM
                    </Button>}
                  <Button size="small" disabled={!canEdit} onClick={() => selectEdit(row)}>Correct</Button>
                  <Button color="error" size="small" disabled={!canEdit} onClick={() => selectVoid(row)}>Remove</Button>
                  <Button size="small" onClick={() => showAudit(row)} disabled={busy}>History</Button>
                </Stack>
              </TableCell>
            </TableRow>;
          })}
          {!rows.length && <TableRow><TableCell colSpan={6}>No saved receipt rows in this batch.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
    {unresolved.length > 0 && <Alert severity="warning" role="alert">
      {unresolved.length} legacy or unmatched staged row(s) have no confirmed exact catalogue identity. Correct these in the original Bulk Intake workflow or move confirmed Quick Intake receipts into a new batch before using Quick Finalise. No automatic guessing is allowed.
    </Alert>}
    {!batchOpen && <Alert severity="info">Received quantities, lots and costs are immutable here. Older receipts with missing condition may be reviewed as Near Mint only after physical verification, with an audit entry. Stock discrepancies require the Inventory Board adjustment process.</Alert>}
    {batchOpen && <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography fontWeight={600}>Finalise Stock — explicit physical receipt</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Confirm {pending.length} pending rows ({totals.staged} physical cards). Removed rows are excluded. This applies inventory lots/weighted average cost exactly once, with no listing allocation or eBay push.
      </Typography>
      {pending.length > 0 && <Typography variant="body2" sx={{ mt: 1 }}>
        Receipt cost review: {pending.map((row) => `#${row.id}: ${row.quantity} × ${row.unit_cost == null ? "COST MISSING" : `$${Number(row.unit_cost).toFixed(4)}`}`).join(" · ")}
      </Typography>}
      {zeroCostRows.length > 0 && <FormControlLabel control={<Checkbox checked={zeroCostReviewed}
        onChange={(e) => setZeroCostReviewed(e.target.checked)} disabled={!canChange} />}
        label={`I checked each of the ${zeroCostRows.length} zero-cost row(s) and explicitly approve $0.0000 as the actual cost`} />}
      <FormControlLabel control={<Checkbox checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
        disabled={!canChange || !pending.length || Boolean(unresolved.length) || Boolean(unknownQuickConditions.length) || Boolean(costInvalidRows.length)} />}
        label="I physically verified this batch and confirm the pending card counts, identities and unit costs" />
      <Box><Button variant="contained" color="primary" disabled={!canChange || !confirm || !pending.length || Boolean(unresolved.length) || Boolean(unknownQuickConditions.length) ||
          Boolean(costInvalidRows.length) || (zeroCostRows.length > 0 && !zeroCostReviewed)} onClick={() => setFinaliseDialog(true)}>
        {busy ? "Working…" : "FINALISE STOCK · RECEIVE PHYSICAL CARDS"}
      </Button></Box>
    </Box>}
    <QuickTrainerConfirmDialog open={finaliseDialog} title="Confirm physical card stock receipt"
      description={`Receive ${totals.staged} physical cards across ${pending.length} rows, with reviewed costs. ${zeroCostRows.length} row(s) use an expressly approved $0.0000 cost. This writes inventory/lot records once and does not push to eBay or allocate listings. Cancel makes no changes.`}
      busy={busy} confirmLabel="Confirm physical receipt" onCancel={() => setFinaliseDialog(false)} onConfirm={finalise} />
    <Dialog open={Boolean(active)} onClose={() => !busy && reset()} maxWidth="md" fullWidth>
      <DialogTitle>{active?.mode === "correct" ? `Correct staged row #${active.row.id}` :
        active?.mode === "void" ? `Remove staged row #${active.row.id}` :
        active?.mode === "confirm_nm" ? `Verify received row #${active.row.id} as Near Mint` : `Audit history for row #${active?.row?.id}`}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
        {active?.mode === "audit" && (audit ? (audit.length ? audit.map((event) => <Alert key={event.id} severity="info">
          {event.action} · {event.created_at ? new Date(event.created_at).toLocaleString("en-AU") : ""} · {event.reason}
          <Typography variant="caption" display="block">Before: {event.before_json}</Typography>
          <Typography variant="caption" display="block">After: {event.after_json}</Typography>
        </Alert>) : <Typography>No changes recorded for this row.</Typography>) : <Typography>Loading history…</Typography>)}
        {active?.mode === "correct" && draft && <>
          <Alert severity="info">Current exact print: {draft.name_and_number} — {draft.set_name}. Changes require selecting an exact Find Card result; quantity and finish can be changed directly.</Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField size="small" fullWidth label="Find replacement exact card" value={lookup} onChange={(e) => setLookup(e.target.value)} />
            <Button variant="outlined" onClick={search} disabled={busy || lookup.trim().length < 2}>Find Card</Button>
          </Stack>
          {matches.length > 0 && <TableContainer sx={{ maxHeight: 185 }}><Table size="small"><TableBody>
            {matches.map((match) => <TableRow key={match.id}><TableCell>{match.name_and_number} — {match.set_name}</TableCell>
              <TableCell align="right"><Button size="small" onClick={() => {
                setDraft((old) => ({ ...old, catalog_card_id: match.id, name_and_number: match.name_and_number, set_name: match.set_name }));
                setMatches([]); setLookup("");
              }}>Select exact print</Button></TableCell></TableRow>)}
          </TableBody></Table></TableContainer>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <FormControl size="small" fullWidth><InputLabel id="correct-finish">Finish</InputLabel><Select labelId="correct-finish" label="Finish"
              value={draft.card_variant || ""} onChange={(e) => setDraft((old) => ({ ...old, card_variant: e.target.value }))}>
              {FINISHES.map((finish) => <MenuItem key={finish} value={finish}>{finish}</MenuItem>)}
            </Select></FormControl>
            <TextField size="small" fullWidth label="Corrected quantity" type="number" inputProps={{ min: 1, step: 1 }} value={draft.quantity}
              onChange={(e) => setDraft((old) => ({ ...old, quantity: e.target.value }))} />
            <TextField size="small" fullWidth label="Batch unit cost (AUD)" value={draft.unit_cost}
              InputProps={{ readOnly: true }} disabled helperText="Use Batch unit COGS above to change the cost of every card in this batch." />
          </Stack>
          <FormControl fullWidth size="small"><InputLabel id="correct-source">Source</InputLabel><Select labelId="correct-source" label="Source" value={draft.source_type}
            onChange={(e) => setDraft((old) => ({ ...old, source_type: e.target.value }))}>
            {SOURCES.map((source) => <MenuItem key={source} value={source}>{source}</MenuItem>)}
          </Select></FormControl>
          {[["source_reference", "Photo/source reference"], ["storage_location", "Storage location"], ["notes", "Notes"]].map(([key, label]) =>
            <TextField key={key} size="small" label={label} fullWidth value={draft[key]}
              onChange={(e) => setDraft((old) => ({ ...old, [key]: e.target.value }))} />)}
        </>}
        {active?.mode === "confirm_nm" && <Alert severity="warning">
          This older receipt has no recorded condition. Physically inspect all {active.row.quantity} received card(s) and confirm they are Near Mint. Only receipt condition metadata will be corrected, with before/after audit history; no stock, cost, allocation or eBay changes. If not Near Mint, close and investigate separately.
        </Alert>}
        {active?.mode === "confirm_nm" && <FormControlLabel control={<Checkbox checked={nearMintConfirmed}
          onChange={(e) => setNearMintConfirmed(e.target.checked)} />} label="I physically inspected these cards and confirm all are Near Mint (NM)" />}
        {(active?.mode === "correct" || active?.mode === "void" || active?.mode === "confirm_nm") && <TextField size="small" fullWidth required
          label="Reason for correction/removal (audited)" value={draft?.reason || ""}
          onChange={(e) => setDraft((old) => ({ ...old, reason: e.target.value }))} />}
        {active?.mode === "void" && <Alert severity="warning">This removes the row from staged totals without deleting its request ID or history. Physical inventory is not changed.</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={reset}>Close</Button>
        {(active?.mode === "correct" || active?.mode === "void" || active?.mode === "confirm_nm") && <Button variant="contained"
          color={active.mode === "void" ? "error" : "primary"} disabled={busy || (active.mode === "correct" ? !editValid : (draft?.reason || "").trim().length < 3 || (draft?.reason || "").trim().length > 500 || (active.mode === "confirm_nm" && !nearMintConfirmed))}
          onClick={saveChange}>{active.mode === "void" ? "Confirm remove staged row" : active.mode === "confirm_nm" ? "Confirm audited Near Mint review" : "Confirm save correction"}</Button>}
      </DialogActions>
    </Dialog>
  </Stack>;
}
